import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserService } from '../services/User.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ResetpasswordComponent } from './resetpassword/resetpassword.component';
import { ANGULAR_MODULES } from '../Shared/shared-imports';
import { GeneralService } from '../services/general.service';
import { LoaderComponent } from '../Shared/loader/loader.component';
import { IUserPermissionsModel } from '../Shared/model/AdminUserModel';
import { SharedHTMLSettingsDataService } from '../Shared/sharedHTMLSettingsData.service';
import Swal from 'sweetalert2';
import { VendorService } from '../services/Vendor.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ResetpasswordComponent, ANGULAR_MODULES, LoaderComponent],
  templateUrl: './login.html',
  styleUrl: './login.scss'
})
export class Login implements OnInit,OnDestroy{
  isFocusedUserId = false;
  isFocusedPassword = false;
  loginForm: FormGroup;
  loginError = false;
  public loaderIcon: Promise<boolean>;
  private subscriptions: Subscription[] = [];
  displayUsernameError = false;
  displayPasswordError = false;
  inActive = false;
  showPasswordResetModal = false;
  userId;
  companyName = "";
  confirmMailInput: HTMLInputElement;

  constructor(private formBuilder: FormBuilder,
    private userService: UserService,
    private generalService: GeneralService,
    private toastr: ToastrService,
    private router: Router,
    private vendorService: VendorService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService) {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.getCompanyName();
  }

  getCompanyName() {
    this.subscriptions.push(this.generalService.getCompanyName().subscribe(res => {
      this.companyName = res;
    }));
  }

  validateLoginCredentials() {
    if (!this.loginForm.valid) {
      this.displayUsernameError = this.loginForm.get('username').hasError('required');
      this.displayPasswordError = this.loginForm.get('password').hasError('required');
    }
    else {
      this.displayUsernameError = false;
      this.displayPasswordError = false;
    }
  }

  get email() {
    return this.loginForm.get('email');
  }

  async validateLogin() {
    var passwordResetDays = await firstValueFrom(this.userService.getUserPasswordExpDate());
    this.loaderIcon = Promise.resolve(true);
    if (this.loginForm.valid) {
      this.loginError = false;
      const credentials =
      {
        username: this.loginForm.value['username'],
        password: this.loginForm.value['password'],
      }
      this.subscriptions.push(this.userService.getToken(credentials).subscribe(async (response) => {
        if (response) {
          if (!response.InActive) {
            const expiryDate = response.PasswordExpiryDate
              ? new Date(response.PasswordExpiryDate)
              : null;
            const dbDefaultDateTime = new Date('1900-01-01T00:00:00');
            const passwordExpired =
              expiryDate &&
              expiryDate.getTime() !== dbDefaultDateTime.getTime() &&
              expiryDate.getTime() < new Date().getTime();

            if (passwordExpired && passwordResetDays > 0) {
              this.toastr.error("Your login password has been Expired. Please reset!", $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
              this.userId = response.UserId;
              this.showPasswordResetModal = true;
              this.loaderIcon = Promise.resolve(false);
            } else {
              this.loginError = false;
              if ((!expiryDate || expiryDate.getTime() == dbDefaultDateTime.getTime()) && passwordResetDays > 0) {
                this.updateUserPasswordExpDate(passwordResetDays);
              }
              this.updateCurrentMonthWebUsers()
              localStorage.setItem('ASCUserName', response.Name);
              localStorage.setItem('ASCUserId', response.UserId);
              this.userService.setLoggedInUserName(response.Name);
              this.userService.setLoggedInUserId(response.UserId);
              await this.fetchUserPermissions();
              this.router.navigate(['/dashboard']);
              this.loaderIcon = Promise.resolve(false);
            }
          }
          else {
            this.inActive = response.InActive;
            this.loaderIcon = Promise.resolve(false);
          }
        }
        else {
          this.loginError = true
          this.loaderIcon = Promise.resolve(false);
        }
      }));
    }
    else {
      this.displayUsernameError = this.loginForm.get('username').hasError('required');
      this.displayPasswordError = this.loginForm.get('password').hasError('required');
      this.loaderIcon = Promise.resolve(false);
    }
  }

  forgotPassword(event) {
    event.preventDefault();
    Swal.fire<boolean>({
      title: 'Confirm Mail',
      html: `
       <input type="text" id="email" class="swal2-input" style="width:82%" placeholder="Email">
       <small style="color : #106EBE !important">Your new login password will be sent to your registered email address. <i class="fa fa-warning " style="color: red"></i> Please check your email inbox!!</small>
     `,
      confirmButtonText: 'Confirm',
      focusConfirm: false,
      showCloseButton: true,
      didOpen: () => {
        const popup = Swal.getPopup()!;
        this.confirmMailInput = popup.querySelector('#email') as HTMLInputElement;
        this.confirmMailInput.onkeyup = (event) => event.key === 'Enter' && Swal.clickConfirm();
      },
      preConfirm: () => {
        const confirmMail = this.confirmMailInput?.value?.trim();

        if (!confirmMail) {
          Swal.showValidationMessage('Email cannot be empty.');
          return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(confirmMail)) {
          Swal.showValidationMessage('Please enter a valid email address.');
          return false;
        }

        return confirmMail; // return the value if valid
      }
    }).then((result) => {

      if (result.value) {
        Swal.close();
        this.loaderIcon = Promise.resolve(true);
        this.subscriptions.push(this.userService.ForgotPassword(this.confirmMailInput?.value).subscribe((res: any) => {
          if (res.Success) {
            this.toastr.success(res.Message, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
          }
          else {
            this.toastr.error(res.Message, $localize`Error`, this.sharedHTMLSettings.ToasterConfig)
          }
          this.loaderIcon = Promise.resolve(false);
        }));
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  closeResetModal() {
    this.showPasswordResetModal = false;
  }

  async updateUserPasswordExpDate(passwordResetDays) {
    try {
      this.subscriptions.push(this.userService.updateUserPasswordExpDate(passwordResetDays).subscribe(async (response) => {
      }));
    } catch (error) {
      console.error('Error updating user password expiry Date', error);
    }
  }

  updateCurrentMonthWebUsers() {
    try {
      this.subscriptions.push(this.vendorService.updateCurrentMonthWebUsers().subscribe(async (response) => {
      }));
    } catch (error) {
      console.error('Error updating user password expiry Date', error);
    }
  }

 async fetchUserPermissions() {
    try {
      const userPermission: IUserPermissionsModel = await firstValueFrom(this.generalService.GetUserPermission());
      this.generalService.UserPermissions = userPermission;
      this.generalService.userGroupPermissions.emit(userPermission);
    } catch (error) {
      console.error('Error fetching user permissions', error);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
